import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import Reveal from '@/components/Reveal';
import { TrustBar } from '@/components/TrustBadges';
import { MagnifyingGlass, MapPin, Star, ArrowRight, ShieldCheck, Funnel, X, ChatCircle, Clock, Lightning, Heart, Scales, Check, Sparkle, Tag } from '@phosphor-icons/react';
import { ProviderGridSkeleton } from '@/components/Skeletons';
import { toast } from '@/components/ui/sonner';
import { cachedGet, clearCache } from '@/lib/cache';
import { CATEGORY_GROUPS } from '@/lib/serviceCategories';
import { providerPrice } from '@/lib/pricing';
import { CompareTray, CompareModal } from '@/components/CompareVendors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category hero images for card backgrounds
const CAT_IMAGES = {
  'Venues': 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=600&h=300&fit=crop',
  'Catering': 'https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=300&fit=crop',
  'Private Chefs': 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&h=300&fit=crop',
  'DJs': 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=300&fit=crop',
  'Live Bands': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=300&fit=crop',
  'Performers': 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&h=300&fit=crop',
  'Photographers': 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=600&h=300&fit=crop',
  'Videographers': 'https://images.unsplash.com/photo-1579632652768-6cb9dcf85912?w=600&h=300&fit=crop',
  'Event Decor': 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=600&h=300&fit=crop',
  'Event Planners': 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=600&h=300&fit=crop',
  'Rentals': 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&h=300&fit=crop',
  'Bartenders': 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&h=300&fit=crop',
  'Servers': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=300&fit=crop',
  'Security': 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=300&fit=crop',
  'Cleaners': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=300&fit=crop',
  'Electricians': 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=600&h=300&fit=crop',
  'Plumbers': 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=600&h=300&fit=crop',
  'Gardeners': 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=300&fit=crop',
  'Movers': 'https://images.unsplash.com/photo-1600518464441-9154a4dea21b?w=600&h=300&fit=crop',
  'Tutors': 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=300&fit=crop',
  'Pet Sitters': 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=300&fit=crop',
  'Barbers': 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=600&h=300&fit=crop',
  'Makeup Artists': 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=600&h=300&fit=crop',
  // Singular forms — used by provider signup and the demo catalog
  'DJ': 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=300&fit=crop',
  'Caterer': 'https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=300&fit=crop',
  'Private chef': 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&h=300&fit=crop',
  'Photographer': 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=600&h=300&fit=crop',
  'Videographer': 'https://images.unsplash.com/photo-1579632652768-6cb9dcf85912?w=600&h=300&fit=crop',
  'Bartender': 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&h=300&fit=crop',
  'Event decorator': 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=600&h=300&fit=crop',
  'Decorator': 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=600&h=300&fit=crop',
  'MC / Host': 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&h=300&fit=crop',
  'Lighting & AV': 'https://images.unsplash.com/photo-1504509546545-e000b4a62425?w=600&h=300&fit=crop',
  'Florist': 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=600&h=300&fit=crop',
  'Live musicians': 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600&h=300&fit=crop',
  'Live band': 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=600&h=300&fit=crop',
  'Dancers': 'https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=600&h=300&fit=crop',
  'Performer': 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&h=300&fit=crop',
  'Cleaner': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=300&fit=crop',
  'Event planner': 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=600&h=300&fit=crop',
  'Food truck': 'https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=300&fit=crop',
  'Cake designer': 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=600&h=300&fit=crop',
  'Photobooth': 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=300&fit=crop',
  'Kids entertainment': 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&h=300&fit=crop',
  'Makeup artist': 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=600&h=300&fit=crop',
  'Barber': 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=600&h=300&fit=crop',
  'Electrician': 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=600&h=300&fit=crop',
  'Plumber': 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=600&h=300&fit=crop',
  'Gardener': 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=300&fit=crop',
  'Mover': 'https://images.unsplash.com/photo-1600518464441-9154a4dea21b?w=600&h=300&fit=crop',
  'Tutor': 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=300&fit=crop',
  'Pet sitter': 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=300&fit=crop',
};



const ALL_CATS = CATEGORY_GROUPS.flatMap(g => g.cats);

export default function BrowsePage() {
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [savedIds, setSavedIds] = useState(() => new Set()); // provider user_ids the customer has saved
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [activeGroup, setActiveGroup] = useState('');
  const [minCapacity, setMinCapacity] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [minRating, setMinRating] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Side-by-side compare: the picked providers (max 4) + whether the modal is open.
  const [compare, setCompare] = useState([]);          // array of provider objects
  const [compareOpen, setCompareOpen] = useState(false);
  const COMPARE_MAX = 4;
  const inCompare = (p) => compare.some(c => c.profile_id === p.profile_id);
  const toggleCompare = (e, p) => {
    e.preventDefault(); e.stopPropagation();
    setCompare(prev => {
      if (prev.some(c => c.profile_id === p.profile_id)) return prev.filter(c => c.profile_id !== p.profile_id);
      if (prev.length >= COMPARE_MAX) { toast.error(`You can compare up to ${COMPARE_MAX} at a time.`); return prev; }
      return [...prev, p];
    });
  };
  const removeCompare = (p) => setCompare(prev => prev.filter(c => c.profile_id !== p.profile_id));
  const clearCompare = () => { setCompare([]); setCompareOpen(false); };

  // Geolocation city auto-fill
  useEffect(() => {
    if (searchParams.get('city')) return;
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&localityLanguage=en`);
            const data = await res.json();
            setCity(data.city || data.locality || '');
          } catch (err) { console.error('Geo error:', err); setCity(''); }
        },
        () => setCity(''),
        { timeout: 5000 }
      );
    } else { setCity(''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // `isStale` lets the caller (the effect below) mark a fetch superseded when the
  // filters change mid-flight, so a slow older response can't overwrite the newer
  // one's results (out-of-order race).
  const fetchProviders = useCallback(async (isStale = () => false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (city) params.set('city', city);
      if (search) params.set('search', search);
      if (maxPrice) params.set('max_price', maxPrice);
      if (minRating) params.set('min_rating', minRating);
      if (verifiedOnly) params.set('verified_only', 'true');
      if (sortBy) params.set('sort_by', sortBy);
      const url = `${API}/providers?${params.toString()}&limit=50`;
      const data = await cachedGet(url, {}, 30000); // 30s cache
      if (isStale()) return;                 // a newer filter fetch superseded this one
      setProviders(data.providers || []);
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to fetch providers:', err);
      toast.error('Couldn’t load pros just now — check your connection and try the search again.');
    }
    finally { if (!isStale()) setLoading(false); }
  }, [category, city, search, maxPrice, minRating, verifiedOnly, sortBy]);

  useEffect(() => {
    let stale = false;
    fetchProviders(() => stale);
    return () => { stale = true; };
  }, [fetchProviders]);

  // Load which providers this customer has already saved, so hearts show filled
  useEffect(() => {
    if (!token) { setSavedIds(new Set()); return; }
    axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSavedIds(new Set((r.data || []).map(f => f.user_id).filter(Boolean))))
      .catch(() => {});
  }, [token]);

  const toggleSave = async (e, provider) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { navigate('/auth'); return; }
    // optimistic toggle
    setSavedIds(prev => {
      const next = new Set(prev);
      next.has(provider.user_id) ? next.delete(provider.user_id) : next.add(provider.user_id);
      return next;
    });
    try {
      const r = await axios.post(`${API}/favorites/${provider.user_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSavedIds(prev => {
        const next = new Set(prev);
        r.data.favorited ? next.add(provider.user_id) : next.delete(provider.user_id);
        return next;
      });
    } catch (err) { console.error('Save error:', err); }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchProviders(); };
  const clearAll = () => { setCategory(''); setActiveGroup(''); setSearch(''); setCity(''); setMinCapacity(''); setMaxPrice(''); setSortBy(''); setMinRating(''); setVerifiedOnly(false); };

  // Capacity has no backend param, so filter it client-side over the fetched results.
  const displayed = minCapacity
    ? providers.filter(p => (Number(p.capacity) || 0) >= Number(minCapacity))
    : providers;

  const catIcon = (catName) => ALL_CATS.find(c => c.name === catName)?.Icon || Tag;

  return (
    <div className="min-h-screen bg-background" data-testid="browse-page">
      <Navbar />

      {/* Search header */}
      <div className="border-b border-border bg-hero-wash">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start justify-between gap-4 mb-1.5">
            <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight text-foreground">
              {/* Multi-category filters (e.g. from the landing page's event types) are listed
                  in the "Active" chip below, so keep the heading readable here. */}
              {category && !category.includes('|') ? category : 'Find your event pros'}
            </h1>
            {/* Compare count — shows how many pros are queued, click to open. */}
            {compare.length > 0 && (
              <button
                type="button"
                onClick={() => compare.length >= 2 ? setCompareOpen(true) : null}
                aria-label={`${compare.length} selected to compare`}
                title={compare.length >= 2 ? 'Open comparison' : 'Pick one more to compare'}
                className={`shrink-0 inline-flex items-center gap-2 h-10 px-3.5 rounded-full border border-border bg-card shadow-soft text-sm font-semibold text-foreground btn-anim ${compare.length >= 2 ? 'hover:bg-secondary/60 cursor-pointer' : 'cursor-default'}`}
                data-testid="compare-count-chip">
                <Scales size={16} weight="fill" className="text-primary" />
                <span className="hidden sm:inline">Comparing</span>
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums">
                  {compare.length}
                </span>
              </button>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-5">Every pro is verified, and your payment is protected until the job's done.</p>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2.5 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search services or providers..."
                className="w-full h-11 rounded-xl border border-border bg-card pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="search-input" />
            </div>
            <div className="relative">
              <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="City"
                className="w-full sm:w-44 h-11 rounded-xl border border-border bg-card pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="city-input" />
            </div>
            <button type="submit" className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer" data-testid="search-button">Search</button>
            <button type="button" onClick={() => setShowFilters(!showFilters)} className="h-11 rounded-xl border border-border bg-card px-4 text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-primary btn-anim cursor-pointer flex items-center justify-center gap-1.5">
              <Funnel size={14} /> Filters
            </button>
          </form>

          {/* Filters bar */}
          {showFilters && (
            <div className="flex gap-3 items-end pb-3 flex-wrap" data-testid="advanced-filters">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Max price</label>
                <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="$"
                  className="w-28 h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Min capacity</label>
                <input type="number" value={minCapacity} onChange={e => setMinCapacity(e.target.value)} placeholder="Guests"
                  className="w-28 h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="filter-capacity" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Sort by</label>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="filter-sort">
                  <option value="">Featured</option>
                  <option value="rating">Top rated</option>
                  <option value="price_low">Price: low to high</option>
                  <option value="price_high">Price: high to low</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Min rating</label>
                <select value={minRating} onChange={e => setMinRating(e.target.value)}
                  className="h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="filter-rating">
                  <option value="">Any</option>
                  <option value="4">4.0+</option>
                  <option value="4.5">4.5+</option>
                </select>
              </div>
              <label className="flex items-center gap-2 h-11 cursor-pointer px-1" data-testid="filter-verified">
                <input type="checkbox" checked={verifiedOnly} onChange={e => setVerifiedOnly(e.target.checked)} className="w-4 h-4 accent-primary cursor-pointer" />
                <span className="text-sm text-foreground inline-flex items-center gap-1"><ShieldCheck size={14} className="text-primary" /> Verified only</span>
              </label>
              <button onClick={fetchProviders} className="bg-primary text-primary-foreground rounded-xl px-5 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Apply</button>
            </div>
          )}

          {/* Group tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1.5" data-testid="group-tabs">
            <button onClick={clearAll}
              className={`px-4 py-2.5 min-h-[44px] text-xs font-medium rounded-full whitespace-nowrap btn-anim cursor-pointer border ${!activeGroup && !category ? 'bg-secondary text-primary border-primary/30' : 'bg-card border-border text-muted-foreground hover:bg-secondary/60 hover:text-primary'}`}>
              All
            </button>
            {CATEGORY_GROUPS.map(g => (
              <button key={g.group} onClick={() => {
                if (activeGroup === g.group) { setActiveGroup(''); setCategory(''); }
                else {
                  setActiveGroup(g.group);
                  // Always set category to first cat in group — subcategory chips let user refine
                  if (g.cats.length === 1) {
                    setCategory(g.cats[0].name);
                  } else {
                    // For multi-cat groups, set to all cat names joined — backend will regex match
                    setCategory(g.cats.map(c => c.name).join('|'));
                  }
                }
              }}
                className={`px-4 py-2.5 min-h-[44px] text-xs font-medium rounded-full whitespace-nowrap btn-anim cursor-pointer border flex items-center gap-1.5 ${activeGroup === g.group ? 'bg-secondary text-primary border-primary/30' : 'bg-card border-border text-muted-foreground hover:bg-secondary/60 hover:text-primary'}`}
                data-testid={`group-${g.group.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                {g.Icon && <g.Icon size={15} weight="regular" />} {g.group}
              </button>
            ))}
          </div>

          {/* Subcategory chips */}
          {activeGroup && (
            <div className="flex gap-2 mt-2.5 flex-wrap" data-testid="subcategory-chips">
              {CATEGORY_GROUPS.find(g => g.group === activeGroup)?.cats.map(cat => (
                <button key={cat.name} onClick={() => setCategory(category === cat.name ? '' : cat.name)}
                  className={`px-3.5 py-2 min-h-[44px] text-xs font-medium rounded-full btn-anim cursor-pointer border flex items-center gap-1 ${category === cat.name ? 'bg-secondary text-primary border-primary/30' : 'bg-card border-border text-muted-foreground hover:bg-secondary/60 hover:text-primary'}`}
                  data-testid={`subcat-${cat.name.toLowerCase().replace(/\s/g, '-')}`}>
                  {cat.Icon && <cat.Icon size={14} weight="regular" />} {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* Active filter tags */}
          {(category || search || city) && (
            <div className="flex gap-2 mt-3 items-center flex-wrap">
              <span className="text-xs text-muted-foreground">Active:</span>
              {category && <span className="text-xs rounded-full bg-secondary text-primary font-medium px-3 py-1 flex items-center gap-1.5">{category.split('|').join(' · ')} <X size={11} className="cursor-pointer" onClick={() => setCategory('')} /></span>}
              {city && <span className="text-xs rounded-full bg-secondary text-primary font-medium px-3 py-1 flex items-center gap-1.5">{city} <X size={11} className="cursor-pointer" onClick={() => setCity('')} /></span>}
              {search && <span className="text-xs rounded-full bg-secondary text-primary font-medium px-3 py-1 flex items-center gap-1.5">"{search}" <X size={11} className="cursor-pointer" onClick={() => setSearch('')} /></span>}
              <button onClick={clearAll} className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2 cursor-pointer btn-anim">Clear all</button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TrustBar className="mb-4 border-b border-border/70 pb-4" />
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">{displayed.length} pro{displayed.length !== 1 ? 's' : ''} found</p>
        </div>

        {loading ? (
          <ProviderGridSkeleton count={6} />
        ) : displayed.length === 0 ? (() => {
          // Two very different empty states:
          //  (a) narrow filters (price/rating/city/search/capacity) hid everything
          //      → nudge to loosen filters.
          //  (b) a whole category simply has no vendors yet (only a category picked,
          //      nothing else) → turn the gap into an invitation: recruit a vendor,
          //      or plan with the AI. Reads as "we're growing", not "we're empty".
          const hasNarrowingFilters = !!(search || city || minCapacity || maxPrice || minRating || verifiedOnly);
          const catLabel = category && !category.includes('|') ? category : '';
          if (!hasNarrowingFilters) {
            return (
              <div className="text-center py-14 px-6 bg-card rounded-2xl border border-border/70 shadow-soft fade-up max-w-xl mx-auto" data-testid="empty-category">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  {(() => { const CIcon = catLabel ? catIcon(catLabel) : Sparkle; return <CIcon size={30} weight="regular" className="text-primary" />; })()}
                </div>
                <h3 className="text-lg font-display font-semibold text-foreground mb-1.5">
                  {catLabel ? <>We're adding {catLabel.toLowerCase()} pros near you</> : <>New pros are joining every week</>}
                </h3>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  Bookr is brand new and growing fast. {catLabel ? `Be part of the first wave of ${catLabel.toLowerCase()} on Bookr` : 'More vendors go live every week'} — or let our AI plan your event with who's available today.
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
                  <Link to="/ai-planner" className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer no-underline inline-flex items-center justify-center gap-1.5">
                    <Lightning size={15} weight="fill" /> Plan with AI
                  </Link>
                  <Link to="/join" className="border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-secondary/60 hover:text-primary cursor-pointer no-underline inline-flex items-center justify-center gap-1.5" data-testid="invite-vendor-cta">
                    {catLabel ? <>Know a great {catLabel.toLowerCase()}? Invite them</> : <>List your services</>} <ArrowRight size={14} />
                  </Link>
                </div>
                {catLabel && (
                  <button onClick={clearAll} className="mt-5 text-xs text-muted-foreground hover:text-primary underline underline-offset-2 cursor-pointer btn-anim">Browse all categories instead</button>
                )}
              </div>
            );
          }
          return (
            <div className="text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft fade-up">
              <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                <MagnifyingGlass size={22} className="text-primary" />
              </div>
              <p className="text-muted-foreground mb-1">No pros match these filters.</p>
              <p className="text-xs text-muted-foreground mb-4">Try widening your price, rating, or location.</p>
              <button onClick={clearAll} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Clear filters</button>
            </div>
          );
        })() : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="providers-grid">
            {displayed.map((provider, i) => {
              const name = provider.business_name || '';
              // Show the vendor's OWN photo — cover first, then their first portfolio
              // shot, then their profile picture. Only fall back to a category stock
              // image if they haven't uploaded anything yet.
              const rawImg = provider.cover_image
                || (provider.portfolio_images && provider.portfolio_images[0])
                || provider.user?.avatar_url
                || CAT_IMAGES[provider.category]
                || CAT_IMAGES['Venues'];
              // Uploaded files are stored as relative /api/files/… paths; the backend
              // lives on a different origin than the site, so prefix them. Full
              // https:// stock URLs are left as-is.
              const img = rawImg && rawImg.startsWith('/')
                ? `${process.env.REACT_APP_BACKEND_URL}${rawImg}`
                : rawImg;
              // AI-detected focal point (keyed by the raw path) keeps the crop on
              // the subject; undefined → the card centers the crop as before.
              const focus = (provider.image_focus || {})[rawImg];
              const imgPos = Array.isArray(focus) && focus.length === 2 ? `${focus[0]}% ${focus[1]}%` : undefined;
              const isSaved = savedIds.has(provider.user_id);

              return (
                <Reveal key={provider.profile_id}
                  delay={(i % 3) * 80}
                  className="group bg-card rounded-2xl border border-border/70 shadow-soft card-lift overflow-hidden flex flex-col"
                  data-testid={`provider-card-${provider.profile_id}`}>

                  {/* Big image */}
                  <Link to={`/provider/${provider.profile_id}`} className="relative block h-44 overflow-hidden">
                    <img src={img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" style={imgPos ? { objectPosition: imgPos } : undefined} />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 via-transparent to-transparent" />
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                      {provider.featured && (
                        <span className="inline-flex items-center gap-1 bg-accent-coral text-primary-foreground px-2.5 py-1 text-[11px] font-semibold rounded-full shadow-soft" data-testid={`featured-badge-${provider.profile_id}`}>
                          <Star size={11} weight="fill" /> Featured
                        </span>
                      )}
                      {provider.verified && (
                        <span className="inline-flex items-center gap-1 glass px-2.5 py-1 text-[11px] font-semibold text-foreground rounded-full shadow-soft">
                          <ShieldCheck size={11} weight="fill" className="text-primary" />
                          {provider.rating_avg >= 4.8 ? 'Top rated' : 'Verified'}
                        </span>
                      )}
                    </div>
                    {/* Capacity + Save */}
                    <div className="absolute top-3 right-3 flex items-center gap-1.5">
                      {provider.capacity && (
                        <span className="bg-foreground/60 backdrop-blur text-background px-2.5 py-1 text-[11px] font-medium rounded-full">
                          {provider.capacity} guests
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => toggleSave(e, provider)}
                        aria-label={isSaved ? 'Remove from saved' : 'Save provider'}
                        aria-pressed={isSaved}
                        className="w-8 h-8 rounded-full bg-card/90 backdrop-blur flex items-center justify-center shadow-soft btn-anim hover:bg-card cursor-pointer"
                        data-testid={`save-${provider.profile_id}`}>
                        <Heart size={15} weight={isSaved ? 'fill' : 'regular'} className={isSaved ? 'text-accent-coral' : 'text-muted-foreground'} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => toggleCompare(e, provider)}
                        aria-label={inCompare(provider) ? 'Remove from compare' : 'Add to compare'}
                        aria-pressed={inCompare(provider)}
                        title="Compare"
                        className={`h-8 px-2.5 rounded-full backdrop-blur flex items-center gap-1 text-[11px] font-semibold shadow-soft btn-anim cursor-pointer ${inCompare(provider) ? 'bg-primary text-primary-foreground' : 'bg-card/90 text-muted-foreground hover:bg-card'}`}
                        data-testid={`compare-${provider.profile_id}`}>
                        {inCompare(provider) ? <Check size={13} weight="bold" /> : <Scales size={13} />} Compare
                      </button>
                    </div>
                    {/* Price overlay — single source of truth for amount + unit.
                        Quote → "Custom quote"; range → "$100–$500"; else "$100/hr". */}
                    <div className="absolute bottom-3 left-3">
                      {(() => { const pr = providerPrice(provider); return (
                      <span className="glass text-foreground px-3 py-1 text-sm font-display font-semibold rounded-full shadow-soft tabular-nums">
                        {pr.isQuote
                          ? <span className="font-sans font-medium">Custom quote</span>
                          : pr.isRange
                            ? pr.display
                            : <>${pr.amount}<span className="text-xs font-sans font-normal text-muted-foreground">{pr.suffixShort}</span></>}
                      </span>
                      ); })()}
                    </div>
                    {/* Category icon */}
                    <div className="absolute bottom-3 right-3">
                      <span className="glass w-9 h-9 rounded-full flex items-center justify-center shadow-soft text-foreground">
                        {(() => { const CIcon = catIcon(provider.category); return <CIcon size={18} weight="regular" />; })()}
                      </span>
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    <Link to={`/provider/${provider.profile_id}`} className="no-underline flex-1">
                      {/* Name + rating */}
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-base font-display font-semibold text-foreground group-hover:text-primary transition-colors duration-300 leading-snug">
                          {name}
                        </h3>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <Star size={13} weight="fill" className="text-amber-400" />
                          <span className="text-xs font-semibold text-foreground tabular-nums">{provider.rating_avg}</span>
                          <span className="text-[11px] text-muted-foreground tabular-nums">({provider.review_count})</span>
                        </div>
                      </div>

                      {/* Category chip */}
                      <div className="mb-2">
                        <span className="rounded-full bg-secondary text-primary text-xs font-medium px-3 py-1 inline-flex items-center">{provider.category}</span>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-1 leading-relaxed">{provider.description}</p>

                      {/* Service tags */}
                      {provider.subcategories?.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap mb-3">
                          {provider.subcategories.slice(0, 3).map((s, si) => (
                            <span key={si} className="text-[10px] rounded-full bg-secondary/60 text-primary px-2.5 py-0.5 font-medium">{s}</span>
                          ))}
                        </div>
                      )}

                      {provider.special_offer && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent-coral bg-accent-coral/10 rounded-full px-2.5 py-1 mb-1.5" data-testid={`offer-chip-${provider.profile_id}`}>
                          <Lightning size={10} weight="fill" /> {provider.special_offer}
                        </span>
                      )}
                      {/* Real location signal (fabricated availability/booking counts removed) */}
                      {provider.city && (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <MapPin size={11} /> {provider.city}
                        </div>
                      )}
                    </Link>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 mt-4 border-t border-border/70">
                      <Link to={`/provider/${provider.profile_id}`}
                        className="flex-1 bg-primary text-primary-foreground text-center py-2.5 min-h-[44px] text-xs font-semibold rounded-xl btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 no-underline flex items-center justify-center gap-1.5 cursor-pointer"
                        data-testid={`book-${provider.profile_id}`}>
                        <ArrowRight size={12} /> {providerPrice(provider).isQuote ? 'Request a Quote' : 'View & Book'}
                      </Link>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(user ? `/messages/${provider.user_id}` : '/auth'); }}
                        className="flex-1 border border-border bg-card text-muted-foreground text-center py-2.5 min-h-[44px] text-xs font-semibold rounded-xl btn-anim hover:bg-secondary/60 hover:text-primary flex items-center justify-center gap-1.5 cursor-pointer"
                        data-testid={`message-${provider.profile_id}`}>
                        <ChatCircle size={12} /> Message
                      </button>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      {/* Side-by-side compare: floating tray + full-screen table */}
      <CompareTray items={compare} max={COMPARE_MAX} onRemove={removeCompare} onClear={clearCompare} onOpen={() => setCompareOpen(true)} />
      {compareOpen && <CompareModal items={compare} onRemove={removeCompare} onClose={() => setCompareOpen(false)} />}
    </div>
  );
}
